import { type PropsWithChildren } from "react";
import { SWRConfig } from "swr";

/**
 * Global SWR defaults.
 *
 * SWR's own default for `errorRetryCount` is `undefined`, which means retry
 * *forever*. Until this provider existed the app had no `SWRConfig` at all, so
 * every hook inherited that — and a permanently failing request (most often a
 * field the caller's role is denied, which fails the whole GraphQL response)
 * turned into an unbounded retry loop.
 *
 * That loop is what escalated a single denied field into a dead session:
 * every retry calls `getAccessToken()`, auth0-spa-js serialises token
 * acquisition behind a Web Lock with a 5s abort, and once callers start
 * timing out on that lock the token accessor returns `undefined` — at which
 * point `graphQLFetcher` sends requests with no Authorization header at all
 * and AppSync rejects every one of them. Bounding the retries keeps a failing
 * widget a failing widget.
 *
 * Individual hooks may still override these (e.g. `next-board-meeting.ts`
 * turns retries off entirely); a per-hook option always wins over this config.
 *
 * Freshness policy (PAD-393): revalidation stays ON everywhere. Read-only
 * list hooks opt into polling with a per-hook `refreshInterval`; edit-context
 * hooks rely on focus/navigation revalidation only. Screens with editable
 * fields must seed local form state through `useSeededState`, which holds a
 * dirty form steady across revalidations and re-seeds a clean one — never
 * turn revalidation off to protect a form. Idle polls that return unchanged
 * data are no-ops: SWR's deep `compare` keeps the previous `data` reference
 * (which requires transforms to live inside the fetcher, the repo convention).
 */
export default function SwrProvider({ children }: PropsWithChildren) {
  return (
    <SWRConfig
      value={{
        errorRetryCount: 3,
        // Back off rather than hammering: ~1s, 2s, 4s, capped. SWR multiplies
        // this by 2^retryCount and applies its own jitter.
        errorRetryInterval: 1000,
        // Focus revalidation stays enabled, but at most once per 30s so
        // alt-tab-heavy users don't hammer AppSync (SWR default is 5s).
        focusThrottleInterval: 30_000,
        // Collapse duplicate requests from multiple components sharing a key.
        dedupingInterval: 5_000,
      }}
    >
      {children}
    </SWRConfig>
  );
}
